import { supabase } from '@services/supabase/supabaseClient';
import { Database } from '@services/supabase/database.types';

type WatchlistItem = Database['public']['Tables']['user_watchlist']['Row'];
type WatchlistInsert = Database['public']['Tables']['user_watchlist']['Insert'];

export interface WatchlistStock {
  id: string;
  ticker: string;
  companyName: string;
  alertPriceHigh: number | null;
  alertPriceLow: number | null;
  alertEnabled: boolean;
  notes: string | null;
  displayOrder: number;
  createdAt: string;
}

/**
 * Watchlist Service
 * Manages user's favorite stocks with price alerts and custom ordering
 */
class WatchlistService {
  /**
   * Get user's watchlist
   */
  async getWatchlist(
    userId: string
  ): Promise<{ data: WatchlistStock[] | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('user_watchlist')
        .select('*')
        .eq('user_id', userId)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching watchlist:', error);
        return { data: null, error: error.message };
      }

      // Transform to our interface
      const watchlist: WatchlistStock[] = (data || []).map(item => ({
        id: item.id,
        ticker: item.ticker,
        companyName: item.company_name,
        alertPriceHigh: item.alert_price_high,
        alertPriceLow: item.alert_price_low,
        alertEnabled: item.alert_enabled || false,
        notes: item.notes,
        displayOrder: item.display_order,
        createdAt: item.created_at,
      }));

      return { data: watchlist, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch watchlist';
      console.error('Watchlist fetch error:', err);
      return { data: null, error: message };
    }
  }

  /**
   * Get watchlist with live prices (uses database function)
   */
  async getWatchlistWithPrices(
    userId: string
  ): Promise<{ data: any[] | null; error: string | null }> {
    try {
      const { data, error } = await supabase.rpc('get_user_watchlist_with_prices', {
        p_user_id: userId,
      });

      if (error) {
        console.error('Error fetching watchlist with prices:', error);
        return { data: null, error: error.message };
      }

      return { data: data || [], error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch watchlist with prices';
      console.error('Watchlist with prices error:', err);
      return { data: null, error: message };
    }
  }

  /**
   * Add stock to watchlist
   */
  async addToWatchlist(
    userId: string,
    ticker: string,
    companyName: string
  ): Promise<{ data: WatchlistStock | null; error: string | null }> {
    try {
      // Get current max display_order
      const { data: existing } = await supabase
        .from('user_watchlist')
        .select('display_order')
        .eq('user_id', userId)
        .order('display_order', { ascending: false })
        .limit(1);

      const nextOrder = existing && existing.length > 0 ? existing[0].display_order + 1 : 0;

      const insertData: WatchlistInsert = {
        user_id: userId,
        ticker: ticker,
        company_name: companyName,
        display_order: nextOrder,
        alert_enabled: false,
      };

      const { data, error } = await supabase
        .from('user_watchlist')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        // Check for duplicate key error
        if (error.code === '23505') {
          return { data: null, error: 'Stock already in watchlist' };
        }
        console.error('Error adding to watchlist:', error);
        return { data: null, error: error.message };
      }

      const watchlistItem: WatchlistStock = {
        id: data.id,
        ticker: data.ticker,
        companyName: data.company_name,
        alertPriceHigh: data.alert_price_high,
        alertPriceLow: data.alert_price_low,
        alertEnabled: data.alert_enabled || false,
        notes: data.notes,
        displayOrder: data.display_order,
        createdAt: data.created_at,
      };

      return { data: watchlistItem, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add to watchlist';
      console.error('Add to watchlist error:', err);
      return { data: null, error: message };
    }
  }

  /**
   * Remove stock from watchlist
   */
  async removeFromWatchlist(
    userId: string,
    ticker: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from('user_watchlist')
        .delete()
        .eq('user_id', userId)
        .eq('ticker', ticker);

      if (error) {
        console.error('Error removing from watchlist:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove from watchlist';
      console.error('Remove from watchlist error:', err);
      return { success: false, error: message };
    }
  }

  /**
   * Check if ticker is in user's watchlist
   */
  async isInWatchlist(userId: string, ticker: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_watchlist')
        .select('id')
        .eq('user_id', userId)
        .eq('ticker', ticker)
        .single();

      return !error && data !== null;
    } catch (err) {
      console.error('Error checking watchlist:', err);
      return false;
    }
  }

  /**
   * Update price alerts for a stock
   */
  async updatePriceAlerts(
    userId: string,
    ticker: string,
    alertPriceHigh: number | null,
    alertPriceLow: number | null,
    alertEnabled: boolean
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from('user_watchlist')
        .update({
          alert_price_high: alertPriceHigh,
          alert_price_low: alertPriceLow,
          alert_enabled: alertEnabled,
        })
        .eq('user_id', userId)
        .eq('ticker', ticker);

      if (error) {
        console.error('Error updating price alerts:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update price alerts';
      console.error('Update price alerts error:', err);
      return { success: false, error: message };
    }
  }

  /**
   * Reorder watchlist items (drag-to-reorder)
   */
  async reorderWatchlist(
    userId: string,
    tickerOrder: string[]
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase.rpc('reorder_watchlist', {
        p_user_id: userId,
        p_ticker_order: tickerOrder,
      });

      if (error) {
        console.error('Error reordering watchlist:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reorder watchlist';
      console.error('Reorder watchlist error:', err);
      return { success: false, error: message };
    }
  }
}

export const watchlistService = new WatchlistService();
